import axios, { AxiosRequestConfig } from 'axios';
import { push } from 'connected-react-router';
import {
    increaseBlockingCallCounter,
    decreaseBlockingCallCounter,
    newNotificationMessage,
    checkVersion,
} from 'redux/actions';
import { StorageState } from 'redux/reducers';
import { store } from 'redux/store/configureStore';
import { LocalStorage, Routes, BLACK_LIST_AUTH_BACKURL, HttpStatusCode } from '../constants';
import { HttpWrapperOptions } from './options';

type RequestType = 'get' | 'post' | 'put' | 'delete';

const CancelToken = axios.CancelToken;
const cancelRequestBecauseOfNewOne = 'cancel request because of new one';
const initializeTimestamp = new Date().getTime();

const minReloadingIntervalMs = 86400000; // 24 hours
const reloadAfterMs = 1000;

class InternalError {
    constructor(public response: any) {
        // do nothing
    }
}

export class HttpService {
    private cancelations: { [key: string]: any; } = {};

    public showFanCounter = 0;

    public get<T>(url: string, httpWrapperOptions?: HttpWrapperOptions): Promise<T> {
        const queryStringParams: Record<string, string> = {};
        if (httpWrapperOptions?.cacheQueryParams) {
            queryStringParams['_t'] = httpWrapperOptions.cacheQueryParams;
        }
        return this.processRequest<T>(url, 'get', null, httpWrapperOptions, queryStringParams);
    }

    public post<T = void>(url: string, data?: any, httpWrapperOptions?: HttpWrapperOptions): Promise<T> {
        return this.processRequest<T>(url, 'post', data, httpWrapperOptions);
    }

    public put<T = void>(url: string, data?: any, httpWrapperOptions?: HttpWrapperOptions): Promise<T> {
        return this.processRequest<T>(url, 'put', data, httpWrapperOptions);
    }

    public delete<T = void>(url: string, data?: any, httpWrapperOptions?: HttpWrapperOptions): Promise<T> {
        return this.processRequest<T>(url, 'delete', data, httpWrapperOptions);
    }

    private processRequest<T>(
        url: string,
        method: RequestType,
        data?: any,
        httpWrapperOptions = new HttpWrapperOptions(),
        queryParams: any = null): Promise<T> {

        const result = new Promise<T>((resolve, reject) => {
            (async () => {
                if (httpWrapperOptions.wait) {
                    setTimeout(() => {
                        store.dispatch(increaseBlockingCallCounter());
                    }, httpWrapperOptions.waitTimeout);
                }

                const state = store.getState() as StorageState;
                const headers: any = {};
                if (httpWrapperOptions.requestIdentificator) {
                    headers['ppm-identificator'] = httpWrapperOptions.requestIdentificator;
                }

                const token = localStorage.getItem(LocalStorage.token);
                if (token) {
                    headers['x-access-token'] = token;
                }

                headers['domain'] = window.location.origin;

                const axiosRequestConfig: AxiosRequestConfig = Object.assign({}, {
                    method,
                    withCredentials: true,
                    responseType: httpWrapperOptions.responseType,
                    timeout: httpWrapperOptions.timeout,
                    headers,
                    data: (data) ? data : undefined,
                    params: {
                        ...queryParams,
                        ...httpWrapperOptions.queryParams,
                    },
                });

                const normalizedUrl = this.normalizeUrl(url);
                try {
                    let res: any = null;
                    let callCounter = 0;
                    while (true) {
                        try {
                            if (httpWrapperOptions.requestSequenceIdentificator) {
                                if (this.cancelations[httpWrapperOptions.requestSequenceIdentificator]) {
                                    this.cancelations[httpWrapperOptions.requestSequenceIdentificator](cancelRequestBecauseOfNewOne);
                                    this.cancelations[httpWrapperOptions.requestSequenceIdentificator] = null;
                                }
                                axiosRequestConfig.cancelToken = new CancelToken((c) => {
                                    this.cancelations[httpWrapperOptions.requestSequenceIdentificator ?? ''] = c;
                                });
                            }
                            const axiosPromise: Promise<any> = axios(normalizedUrl, axiosRequestConfig);
                            res = await axiosPromise;
                            if (res.status === HttpStatusCode.accepted) {
                                throw new InternalError(res);
                            }
                            if (httpWrapperOptions.requestSequenceIdentificator) {
                                this.cancelations[httpWrapperOptions.requestSequenceIdentificator] = null;
                            }
                            break;
                        } catch (err: any) {
                            if (err.message === cancelRequestBecauseOfNewOne) {
                                res = {
                                    data: null
                                };
                                // console.warn('request canceled by next one');
                                break;
                            }

                            ++callCounter;
                            if (httpWrapperOptions.requestSequenceIdentificator) {
                                if (this.cancelations[httpWrapperOptions.requestSequenceIdentificator]) {
                                    // console.warn('request canceled by next try');
                                    this.cancelations[httpWrapperOptions.requestSequenceIdentificator]();
                                    this.cancelations[httpWrapperOptions.requestSequenceIdentificator] = null;
                                }
                            }

                            if (err.code !== 'ECONNABORTED' || callCounter > httpWrapperOptions.extraTries) {
                                throw err;
                            }
                        }
                    }
                    this.showDialog(httpWrapperOptions, true, undefined);

                    if (res && res.headers && res.headers['web-client-version']
                        && state.resources.settings
                        && res.headers['web-client-version'] !== state.resources.settings.webClientVersion
                        && ((new Date().getTime() - initializeTimestamp) > minReloadingIntervalMs)) {
                        setTimeout(() => window.location.reload(), reloadAfterMs);
                    }
                    if (res && res.headers && res.headers['resource-version']) {
                        store.dispatch(checkVersion(+res.headers['resource-version']));
                    }
                    resolve(res.data);
                } catch (reason: any) {
                    const status = (reason.response && reason.response.status) ? reason.response.status : HttpStatusCode.exception;

                    if (status === HttpStatusCode.unauthorized && !BLACK_LIST_AUTH_BACKURL.includes(window.location.pathname as Routes)) {
                        const backUrlPath = `${Routes.login}?backurl=${encodeURIComponent(window.location.href)}`;
                        store.dispatch(push(backUrlPath));
                    }
                    reject(reason.response || reason);
                }
                finally {
                    if (httpWrapperOptions.wait) {
                        store.dispatch(decreaseBlockingCallCounter());
                    }
                }
            })();
        });

        return result;
    }

    private showDialog(httpWrapperOptions: HttpWrapperOptions, success: boolean, text?: string) {
        if (success) {
            if (httpWrapperOptions.success) {
                store.dispatch(newNotificationMessage(
                    {
                        message: httpWrapperOptions.successText || text || 'Какая то информация',
                        messageType: 'error',
                        title: 'Информация'
                    }
                ));
            }
        } else {
            if (httpWrapperOptions.errors) {
                store.dispatch(newNotificationMessage(
                    {
                        message: httpWrapperOptions.errorText || text || 'Неопознанная ошибка',
                        messageType: 'error',
                        title: 'Ошибка'
                    }
                ));
            }
        }
    }

    private normalizeUrl(url: string): string {
        if (!url.startsWith('http')) {
            return `${process.env.REACT_APP_BACKEND_URL}${url}`;
        } else {
            return `${url}`;
        }
    }
}
